import React, { useState } from 'react';

/**
 * SilasToast - Factual errors, no drama
 *
 * DOCTRINE:
 * - No shaking. No red explosions. No apologies. No server-speak.
 * - One factual sentence
 * - One next step (if available)
 * - NO green for success — amber accent only
 *
 * Silas voice examples:
 * ✅ "I can't verify your session."
 * ❌ "Oops! Something went wrong!"
 * ✅ "Rate applied to 14 dates."
 * ❌ "Awesome! Your changes were saved!"
 * ✅ "Connection interrupted. Retrying."
 * ❌ "Uh oh! We lost connection!"
 */

export type SilasToastType = 'info' | 'success' | 'error';

export interface SilasToastProps {
  /** Toast variant */
  variant?: SilasToastType;
  /** Toast content - factual, no drama */
  children: React.ReactNode;
  /** Callback when dismiss button clicked */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// Left border colors by variant
// INFO and SUCCESS both use amber (--accent) — amber = "actioned", not "good"
const variantBorder: Record<SilasToastType, string> = {
  info: 'border-l-[var(--accent)]',
  success: 'border-l-[var(--accent)]', // NOT green — amber means "actioned"
  error: 'border-l-red-500',
};

export function SilasToast({
  variant = 'info',
  children,
  onDismiss,
  className = '',
}: SilasToastProps) {
  return (
    <div
      className={`
        bg-[var(--surface-raised)]
        border border-[var(--border-color)]
        border-l-2 ${variantBorder[variant]}
        rounded-sm
        px-4 py-3
        text-sm text-[var(--text-primary)]
        flex items-start justify-between gap-3
        shadow-lg shadow-black/30
        ${className}
      `.trim()}
      role="alert"
    >
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-xs uppercase tracking-wider transition-colors duration-150"
          aria-label="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// Toast context for global toast management
export interface ToastContextValue {
  showToast: (props: Omit<SilasToastProps, 'onDismiss'> & { duration?: number }) => void;
  hideToast: () => void;
}

export const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastState {
  variant?: SilasToastType;
  children: React.ReactNode;
  duration?: number;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (props: Omit<SilasToastProps, 'onDismiss'> & { duration?: number }) => {
    setToast(props);

    // Auto-dismiss after duration (default 5s)
    if (props.duration !== 0) {
      setTimeout(() => {
        setToast(null);
      }, props.duration ?? 5000);
    }
  };

  const hideToast = () => {
    setToast(null);
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
          <SilasToast variant={toast.variant} onDismiss={hideToast}>
            {toast.children}
          </SilasToast>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export default SilasToast;
